import { Injectable } from '@angular/core';
import { map, Observable, throwError } from 'rxjs';
import { BaseWooCommerceCountryStatePostcode } from '../../interfaces/woo-commerce/woo-commerce.interface';
import { WooCommerceShippingParameters } from '../../interfaces/woo-commerce/shipping/shipping.interface';
import {
  WooCommerceShipping,
  WooCommerceShippingMethod
} from '../../interfaces/woo-commerce/api/shipping/shipping-api.interface';
import { WooCommerceShippingApiService } from './api/woo-commerce-shipping-api.service';
import { ShippingOption } from '../../../modules/checkout/models/delivery-methods';
import { HappyDeliveryCheckRequest } from '../../interfaces/happy-api/happy-api.interface';

export interface WooCommerceShippingMethods {
  deliveryMethods: ShippingOption[];
  pickupMethods: ShippingOption[];
}

@Injectable({ providedIn: 'root' })
export class WooCommerceShippingService {
  constructor(
    private wooCommerceShippingApiService: WooCommerceShippingApiService
  ) {}

  /**
   * Check delivery availability for an address via POST /delivery-check,
   * returning the result in the same WooCommerceShippingMethods shape.
   */
  checkDeliveryAvailability(
    data?: BaseWooCommerceCountryStatePostcode
  ): Observable<WooCommerceShippingMethods> {
    if (!data) {
      return throwError(() => 'Shipping Address is not provided!');
    }

    const body: HappyDeliveryCheckRequest = {
      country_code: data.country || 'US',
      state_code: data.state || '',
      postcode: data.postcode || '',
      city: data.city || ''
    };

    return this.wooCommerceShippingApiService.deliveryCheck(body).pipe(
      map((response) => {
        const methods =
          WooCommerceShippingApiService.mapDeliveryCheckToShippingMethods(
            response
          );
        return this.groupShippingMethods(methods);
      })
    );
  }

  getShippingMethods(
    data?: BaseWooCommerceCountryStatePostcode
  ): Observable<WooCommerceShippingMethods> {
    if (!data) {
      return throwError(() => 'Shipping Address is not provided!');
    }

    const payload: WooCommerceShippingParameters = {
      state: data?.state || '',
      country: data?.country || 'US',
      postcode: data?.postcode || '',
      address:
        data?.address ||
        `${data.city} ${data.state} ${data.postcode} ${data.country}`,
      city: data?.city || ''
    };

    return this.wooCommerceShippingApiService.getShippingMethods(payload).pipe(
      map(({ shipping_methods }: WooCommerceShipping) => {
        return this.groupShippingMethods(shipping_methods);
      })
    );
  }

  private groupShippingMethods(
    shipping_methods: WooCommerceShippingMethod[]
  ): WooCommerceShippingMethods {
    const deliveryMethods: ShippingOption[] = [];
    const pickupMethods: ShippingOption[] = [];
    const storedMethods: Record<string, boolean> = {};

    shipping_methods.forEach((method: WooCommerceShippingMethod) => {
      if (storedMethods[method.id]) {
        return;
      }

      const price = method.cost ? parseFloat(method.cost) : 0;

      const deliveryOption: ShippingOption = {
        id: `${method.id}:${method.instance_id}`,
        name: method.title,
        description: method.method_description,
        cost: method.cost,
        price: price,
        icon: method.id.includes('doordash')
          ? 'icon-doordash'
          : method.id.includes('uber')
            ? 'icon-uber'
            : 'icon-delivery-outline',
        settings: method.settings
          ? {
              minimum_order_amount: method.settings.minimum_order_amount,
              maximum_order_amount: method.settings.maximum_order_amount,
              cart_base_min_enabled: method.settings.cart_base_min_enabled,
              cart_base_min_products: method.settings.cart_base_min_products
            }
          : undefined
      };

      if (method.id.includes('pickup')) {
        pickupMethods.push(deliveryOption);
      } else {
        deliveryMethods.push(deliveryOption);
      }

      storedMethods[method.id] = true;
    });

    return { deliveryMethods, pickupMethods };
  }
}
